<template>
    <div class="mb-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex flex-nowrap items-center gap-2">
                <span class="w-32 shrink-0 text-muted">{{ trans('quality_assurance.configuration.modal.deadline.complete_within') }}</span>
                <input
                    v-model="duration.amount"
                    :aria-label="trans('quality_assurance.configuration.modal.deadline.complete_within')"
                    :disabled="!startDate?.isValid"
                    :min="duration.unit === 'minutes' ? MIN_WINDOW_MINUTES : 1"
                    class="form-control !w-20"
                    name="window-amount"
                    type="number"
                    @blur="onAmountBlur"
                />
                <div class="w-40">
                    <jet-select
                        :disabled="!startDate?.isValid"
                        :enable-search="false"
                        :horizontal="false"
                        :model-value="duration.unit"
                        :options="unitOptions"
                        :required="true"
                        form-group-classes="mb-0"
                        @update:model-value="onUnitChange"
                    />
                </div>
            </div>
            <small v-if="computedDueHint" class="text-muted">{{ computedDueHint }}</small>
        </div>
        <div v-if="invalidFeedback" class="mt-1 text-sm text-[13px] text-danger" v-text="invalidFeedback" />
    </div>
</template>

<script setup>
import fullDate from '@/filters/fullDate';
import JetSelect from '@/Jetstream/Select.vue';
import { trans } from '@/mixins/i18n';
import { addWindow, clampWindowAmount, decomposeWindow, MIN_WINDOW_MINUTES } from '@/Utils/scheduleWindow';
import { usePage } from '@inertiajs/vue3';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';

const props = defineProps({
    modelValue: DateTime,
    startDate: DateTime,
    invalidFeedback: String,
});

const emit = defineEmits(['update:modelValue']);

const page = usePage();

const defaultDuration = () => ({ amount: 1, unit: 'days' });

// Any valid stored window decomposes losslessly (minutes is the catch-all), so
// editing reproduces the saved deadline exactly and never dirties the form.
const duration = ref(decomposeWindow(props.startDate, props.modelValue) ?? defaultDuration());

const unitOptions = computed(() => {
    const amount = Number(duration.value.amount) || 1;

    return ['minutes', 'hours', 'days', 'weeks', 'months'].map((unit) => {
        // Capitalize so the dropdown reads "Month" like the frequency options,
        // without capitalizing the shared general.unit.* keys used mid-sentence.
        const label = trans(`general.unit.${unit}`, { '%d': amount });

        return { value: unit, label: label.charAt(0).toUpperCase() + label.slice(1) };
    });
});

const computedDueHint = computed(() => {
    if (!props.modelValue?.isValid) {
        return '';
    }

    return trans('quality_assurance.configuration.modal.deadline.computed_hint', {
        date: fullDate(props.modelValue, page.props.locale),
        // 24-hour to match the form's time inputs (TimePicker uses HH:mm).
        time: props.modelValue.toFormat('HH:mm'),
    });
});

watch([() => props.startDate, () => duration.value.amount, () => duration.value.unit], () => {
    if (!props.startDate?.isValid) {
        return;
    }

    const amount = Number(duration.value.amount);
    if (!Number.isFinite(amount) || amount < 1) {
        return;
    }

    const candidate = addWindow(props.startDate, { amount, unit: duration.value.unit });
    if (candidate.toMillis() === props.modelValue?.toMillis()) {
        return;
    }

    emit('update:modelValue', candidate);
});

const onAmountBlur = () => {
    duration.value = clampWindowAmount({ amount: Number(duration.value.amount), unit: duration.value.unit });
};

// Clamp only on user-initiated unit changes — programmatic seeding (decomposing
// stored data) must never alter the deadline.
const onUnitChange = (unit) => {
    duration.value = clampWindowAmount({ amount: Number(duration.value.amount), unit });
};
</script>
